// Help article content structure
// Using TypeScript objects for simplicity and version control

export interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  categorySlug: string;
  summary: string;
  content: string; // Markdown content
  tags: string[];
  relatedArticles?: string[]; // Article IDs
}

export interface HelpCategory {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string; // MUI icon name
  order: number;
}

export const helpCategories: HelpCategory[] = [
  {
    id: 'getting-started',
    slug: 'getting-started',
    title: 'Getting Started',
    description: 'Learn the basics of Store Control',
    icon: 'RocketLaunch',
    order: 1,
  },
  {
    id: 'store-hours',
    slug: 'store-hours',
    title: 'Store Hours',
    description: 'Manage regular and special hours',
    icon: 'Schedule',
    order: 2,
  },
  {
    id: 'channels',
    slug: 'channels',
    title: 'Channels',
    description: 'Control online ordering and delivery',
    icon: 'DeviceHub',
    order: 3,
  },
  {
    id: 'items',
    slug: 'items',
    title: 'Item Management',
    description: 'Snooze and manage menu items',
    icon: 'Block',
    order: 4,
  },
  {
    id: 'bulk-operations',
    slug: 'bulk-operations',
    title: 'Bulk Operations',
    description: 'Update multiple stores at once',
    icon: 'ContentCopy',
    order: 5,
  },
  {
    id: 'activity',
    slug: 'activity',
    title: 'Activity & History',
    description: 'Track changes and audit logs',
    icon: 'History',
    order: 6,
  },
  {
    id: 'troubleshooting',
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Common issues and solutions',
    icon: 'Build',
    order: 7,
  },
];

export const helpArticles: HelpArticle[] = [
  // Getting Started
  {
    id: 'gs-overview',
    slug: 'overview',
    title: 'What is Store Control?',
    category: 'Getting Started',
    categorySlug: 'getting-started',
    summary: 'A quick introduction to Store Control and what you can do with it.',
    content: `
# What is Store Control?

Store Control is your central hub for managing store operations across all your locations. Whether you manage one store or many, Store Control helps you:

- **Update store hours** for regular days, holidays, and special events
- **Control ordering channels** like online ordering and delivery partners
- **Snooze menu items** when you run out of ingredients
- **Make bulk changes** across multiple stores at once

## Who should use Store Control?

Store Control is designed for:
- **Restaurant General Managers (RGMs)** managing daily operations
- **Area Restaurant Leaders (ARLs)** overseeing multiple locations
- **Operations teams** handling regional updates

## Quick Navigation

- On **desktop**, use the left sidebar to switch between Store Management, Activity Log, and Settings
- On **mobile**, use the bottom navigation to access Snooze Items, Channels, Hours, and More

Ready to get started? Check out "Your First Actions" to learn what you can do right away.
    `,
    tags: ['introduction', 'basics', 'overview'],
    relatedArticles: ['gs-first-actions', 'gs-navigation'],
  },
  {
    id: 'gs-first-actions',
    slug: 'first-actions',
    title: 'Your First Actions',
    category: 'Getting Started',
    categorySlug: 'getting-started',
    summary: 'Get up and running with these quick actions you can take right now.',
    content: `
# Your First Actions

Here are the most common things you'll do in Store Control:

## 1. Check Today's Hours

Your stores' hours for today are shown on the main dashboard. Look for any stores that might need attention.

## 2. Snooze an Item (86)

Running low on an ingredient? Snooze the affected menu items:
1. Go to **Snooze Items** (mobile) or click **86 Items** (desktop)
2. Search for the item
3. Set how long to snooze it
4. The item will automatically return when the timer ends

## 3. Turn Off a Channel

Need to pause delivery orders during a rush?
1. Go to **Channels**
2. Find the channel (e.g., DoorDash)
3. Toggle it off
4. Add a reason (optional but helpful for your team)

## 4. Update Special Hours

Holiday coming up? Set special hours:
1. Go to **Hours**
2. Click **Add Special Hours**
3. Select the date and enter the hours
4. Save your changes

**Tip:** Changes take effect immediately but may take a few minutes to appear on customer-facing apps.
    `,
    tags: ['quick start', 'basics', 'tutorial'],
    relatedArticles: ['gs-overview', 'hours-special'],
  },
  {
    id: 'gs-navigation',
    slug: 'navigation',
    title: 'Navigating the App',
    category: 'Getting Started',
    categorySlug: 'getting-started',
    summary: 'Learn how to find your way around Store Control on desktop and mobile.',
    content: `
# Navigating the App

Store Control adapts to your device for the best experience.

## Mobile Navigation

On mobile, you'll see a **bottom navigation bar** with four tabs:

| Tab | What it does |
|-----|--------------|
| **Snooze Items** | Mark items as unavailable |
| **Channels** | Toggle delivery partners on/off |
| **Hours** | View and edit store hours |
| **More** | Activity log, settings, and logout |

**Tip:** Tap the store name at the top to switch between stores you manage.

## Desktop Navigation

On desktop, you have a **left sidebar** with:

- **Store Management** - Main dashboard with all your stores
- **Activity Log** - See all recent changes
- **Settings** - Preferences like time format

**Tip:** Click your name in the top-right to access My Activity, Settings, and View Tutorial.

## Switching Stores

If you manage multiple stores:
- **Mobile:** Tap the store picker at the top of any screen
- **Desktop:** Click on any store in the store list to see its details
    `,
    tags: ['navigation', 'mobile', 'desktop'],
    relatedArticles: ['gs-overview'],
  },

  // Store Hours
  {
    id: 'hours-regular',
    slug: 'regular-hours',
    title: 'Setting Regular Hours',
    category: 'Store Hours',
    categorySlug: 'store-hours',
    summary: 'How to view and update your store\'s regular operating hours.',
    content: `
# Setting Regular Hours

Regular hours are your store's standard operating schedule that applies every week.

## Viewing Current Hours

1. Go to **Store Management** (desktop) or **Hours** (mobile)
2. Select a store
3. You'll see the weekly schedule with hours for each day

## Editing Regular Hours

1. Click **Edit Hours** or the pencil icon
2. Adjust the open/close times for each day
3. For days you're closed, toggle the day off
4. Click **Save**

## Split Hours (Mid-day Closures)

Some stores close mid-day. To set split hours:

1. Edit the day's hours
2. Click **Add Split** or the + icon
3. Enter your morning hours (e.g., 6:00 AM - 2:00 PM)
4. Enter your evening hours (e.g., 5:00 PM - 10:00 PM)
5. Save your changes

**Important:** Changes to regular hours affect your store going forward. For one-time changes, use Special Hours instead.
    `,
    tags: ['hours', 'schedule', 'regular'],
    relatedArticles: ['hours-special', 'hours-bulk'],
  },
  {
    id: 'hours-special',
    slug: 'special-hours',
    title: 'Adding Special Hours',
    category: 'Store Hours',
    categorySlug: 'store-hours',
    summary: 'Override regular hours for holidays, events, or temporary schedule changes.',
    content: `
# Adding Special Hours

Special hours override your regular schedule for specific dates. Use them for holidays, events, or any temporary changes.

## When to Use Special Hours

- **Holidays** - Thanksgiving, Christmas, New Year's
- **Special events** - Local festivals, game days
- **Temporary closures** - Maintenance, training days
- **Extended hours** - Promotions, busy seasons

## Adding Special Hours

1. Go to **Hours** for your store
2. Click **Add Special Hours** or the + button
3. Select the date (or date range)
4. Choose one of these options:
   - **Custom hours** - Enter specific open/close times
   - **Closed all day** - Store will be closed
   - **24 hours** - Open around the clock
5. Add an optional note (e.g., "Christmas Eve")
6. Click **Save**

## Managing Existing Special Hours

- Special hours appear in a list below regular hours
- Click on any entry to edit it
- Click the trash icon to remove it

**Tip:** Set special hours in advance! You can schedule them weeks or months ahead.
    `,
    tags: ['hours', 'holidays', 'special'],
    relatedArticles: ['hours-regular', 'bulk-hours'],
  },
  {
    id: 'hours-bulk',
    slug: 'bulk-hours',
    title: 'Bulk Hours Updates',
    category: 'Store Hours',
    categorySlug: 'store-hours',
    summary: 'Update hours for multiple stores at the same time.',
    content: `
# Bulk Hours Updates

Managing multiple stores? Update all their hours at once instead of one by one.

## How to Bulk Update Hours

1. Go to **Store Management** on desktop
2. Select multiple stores using the checkboxes
3. Click **Edit Hours** in the action bar
4. Choose what to update:
   - Regular hours for specific days
   - Add special hours for a date
5. Apply the changes

## What Gets Updated

When you bulk update:
- All selected stores receive the same hours
- Existing hours for those days are replaced
- Other days remain unchanged

## Best Practices

- **Group similar stores** - Select stores with similar operating patterns
- **Double-check your selection** - Make sure you've selected the right stores
- **Add notes** - Include context in special hours notes

**Caution:** Bulk updates replace existing hours. Review your selections carefully before applying.
    `,
    tags: ['bulk', 'hours', 'multiple stores'],
    relatedArticles: ['hours-special', 'bulk-overview'],
  },

  // Channels
  {
    id: 'channels-overview',
    slug: 'overview',
    title: 'Understanding Channels',
    category: 'Channels',
    categorySlug: 'channels',
    summary: 'Learn about online ordering and delivery channel management.',
    content: `
# Understanding Channels

Channels control how customers can order from your store. Store Control lets you manage:

## Online Ordering

This controls orders placed through your brand's website and mobile app.

- When **on**, customers can order online for pickup
- When **off**, online ordering is paused

## Delivery Partners

Individual toggles for each delivery platform:

- **DoorDash**
- **Uber Eats**
- **Grubhub**
- And other partners in your market

Each partner can be turned on or off independently.

## When to Turn Off a Channel

- **During a rush** - Too many orders to handle
- **Staff shortage** - Not enough team members
- **Equipment issues** - Oven down, POS problems
- **Bad weather** - Unsafe delivery conditions

**Remember:** Turning off channels affects real orders. Use this thoughtfully and turn them back on when you're ready.
    `,
    tags: ['channels', 'delivery', 'online ordering'],
    relatedArticles: ['channels-toggle', 'channels-reasons'],
  },
  {
    id: 'channels-toggle',
    slug: 'toggle-channels',
    title: 'Toggling Channels On/Off',
    category: 'Channels',
    categorySlug: 'channels',
    summary: 'Step-by-step guide to turning ordering channels on and off.',
    content: `
# Toggling Channels On/Off

## Mobile

1. Tap **Channels** in the bottom navigation
2. Select your store (if you manage multiple)
3. You'll see all available channels with toggle switches
4. Tap a toggle to turn the channel on or off
5. If turning off, you may be asked for a reason

## Desktop

1. Go to **Store Management**
2. Click on a store to see its details
3. Find the **Channels** section
4. Click the toggle next to any channel
5. Confirm the change

## Adding a Pause Reason

When turning off a channel, adding a reason helps your team understand why:

- Select from common reasons (Staff shortage, Equipment issue, etc.)
- Or type a custom reason
- This shows up in the Activity Log

## Re-enabling Channels

Simply toggle the channel back on. No reason required.

**Tip:** Set a reminder to turn channels back on! It's easy to forget after a busy rush.
    `,
    tags: ['channels', 'toggle', 'on off'],
    relatedArticles: ['channels-overview', 'channels-reasons'],
  },
  {
    id: 'channels-reasons',
    slug: 'pause-reasons',
    title: 'Adding Pause Reasons',
    category: 'Channels',
    categorySlug: 'channels',
    summary: 'Why and how to add reasons when pausing channels.',
    content: `
# Adding Pause Reasons

When you turn off a channel, Store Control asks for a reason. This creates accountability and helps your team.

## Why Add a Reason?

- **Accountability** - Shows why decisions were made
- **Team communication** - Others see why channels are off
- **Pattern recognition** - Identify recurring issues
- **Reporting** - ARLs can review pause patterns

## Common Reasons

Choose from these preset options:
- Staff shortage
- Equipment issue
- High volume
- Weather conditions
- Scheduled maintenance
- Other (custom)

## Where Reasons Appear

- In the **Activity Log** for your store
- In **reports** for your area (if applicable)
- As a note next to the paused channel

## Best Practice

Always add a reason, even for brief pauses. It takes seconds and provides valuable context for your team and managers.
    `,
    tags: ['channels', 'reasons', 'accountability'],
    relatedArticles: ['channels-toggle', 'activity-viewing'],
  },

  // Items
  {
    id: 'items-snooze',
    slug: 'snoozing-items',
    title: 'Snoozing Items (86)',
    category: 'Item Management',
    categorySlug: 'items',
    summary: 'How to temporarily remove items from your menu when you run out.',
    content: `
# Snoozing Items (86)

"86" is restaurant industry slang for running out of something. Store Control makes it easy to temporarily remove items from your menu.

## How to Snooze an Item

Mobile and desktop offer the same options — pick as many items as you need, then set one duration for all of them.

### On Mobile
1. Tap **Snooze Items** in the bottom navigation
2. Tap **+** to open the snooze sheet
3. Browse or filter the store's live menu and tick every item you need (or use **Select all shown**)
4. Scroll down and choose a duration — **Snooze** stays disabled until you pick one
5. Tap **Snooze**

### On Desktop
1. Go to a store's detail view — it opens on the **Snooze Items** tab
2. Click **Snooze an Item**
3. Search and tick the items
4. Set the duration and confirm

## Snoozing Only Some Variants

If a product has more than one variant (say Regular and Large), you get a **Snoozing scope** choice for it:

- **Whole product** — every variant goes off the menu
- **Specific variants only** — pick which variants to snooze; the rest stay orderable

Each selected product keeps its own scope, so you can snooze one product entirely and only one variant of another in the same action.

## What Happens When You Snooze

- The item disappears from online ordering
- Delivery partners won't show the item
- Your POS may also reflect the change (depends on integration)

## Auto-Restore

When the snooze timer ends, the item automatically returns to your menu. No action needed!

**Tip:** If you know you'll be out all day, use **End of Day (5 AM)** to avoid manually extending the snooze.
    `,
    tags: ['86', 'snooze', 'items', 'out of stock'],
    relatedArticles: ['items-duration', 'items-bulk'],
  },
  {
    id: 'items-duration',
    slug: 'snooze-duration',
    title: 'Setting Snooze Duration',
    category: 'Item Management',
    categorySlug: 'items',
    summary: 'Choose the right snooze time for different situations.',
    content: `
# Setting Snooze Duration

Choosing the right duration helps customers and keeps your menu accurate.

## Quick Options

The same set is available on mobile and desktop:

| Duration | Best for |
|----------|----------|
| **1 H / 2 H / 4 H** | Prep time, waiting on a delivery |
| **6 H / 12 H** | Out for most of a shift |
| **End of Day (5 AM)** | Out for the rest of the trading day |
| **24 Hours** | Out until this time tomorrow |
| **1 Week** | Supply issue with a known resolution date |
| **1 Month / 2 Months** | Seasonal or long-term unavailability |

Or use **Custom time** to pick an exact date and time.

## Maximum Snooze Length

An item can be snoozed for up to **60 days**. Later dates are greyed out in the calendar, and the
**2 Months** quick option stops at the same ceiling. If you need an item off the menu for longer,
snooze it again when the first snooze expires.

## Extending a Snooze

If you need more time:
1. Find the snoozed item
2. Click on it to edit
3. Choose a new duration
4. Save changes

## Ending a Snooze Early

Got the ingredient back sooner than expected?
1. Find the snoozed item
2. Tap the green tick to **Activate** it — or tick several items and use **Activate (N)**
3. The item returns to your menu immediately

## Best Practice

- **Be realistic** - Don't snooze for 1 hour if you need 4
- **Update if plans change** - Extend or end early as needed
- **Use End of Day wisely** - Only when you're sure you can't get the item that day
    `,
    tags: ['snooze', 'duration', 'timing'],
    relatedArticles: ['items-snooze', 'items-auto-restore'],
  },
  {
    id: 'items-auto-restore',
    slug: 'auto-restore',
    title: 'Auto-Restore Behavior',
    category: 'Item Management',
    categorySlug: 'items',
    summary: 'How items automatically return to your menu.',
    content: `
# Auto-Restore Behavior

Store Control automatically restores snoozed items when their timer ends. Here's how it works.

## How Auto-Restore Works

1. You snooze an item with a duration (e.g., 2 hours)
2. The countdown begins immediately
3. When time expires, the item returns to your menu
4. No action required from you

## What Gets Restored

- The item appears on online ordering
- Delivery partners can see the item again
- The Activity Log shows the restoration

## "End of Day" Behavior

If you chose **End of Day (5 AM)**:
- The item restores at the next 5 AM — the business-day cutoff
- Before 5 AM, that means 5 AM the same morning; otherwise 5 AM tomorrow

## Manual Override

You can always:
- **Activate early** - Tap the green tick on any snoozed item
- **Extend** - Edit the snooze to add more time
- **Snooze again** - If the item auto-restored but you still need it off

**Note:** Auto-restore happens in the background. You don't need to be in the app for it to work.
    `,
    tags: ['auto-restore', 'snooze', 'automatic'],
    relatedArticles: ['items-snooze', 'items-duration'],
  },

  // Bulk Operations
  {
    id: 'bulk-overview',
    slug: 'overview',
    title: 'Bulk Operations Overview',
    category: 'Bulk Operations',
    categorySlug: 'bulk-operations',
    summary: 'Save time by updating multiple stores at once.',
    content: `
# Bulk Operations Overview

Bulk operations let you make the same change across multiple stores simultaneously. This is especially useful for ARLs and operators managing several locations.

## What You Can Bulk Update

- **Store Hours** - Set special hours for holidays across all stores
- **Channels** - Turn off delivery during bad weather region-wide
- **Items** - Snooze an item that's out across your area

## How Bulk Operations Work

1. **Select stores** - Check the boxes next to stores you want to update
2. **Choose action** - Click the appropriate bulk action button
3. **Configure** - Set the details (hours, channels, items)
4. **Apply** - Confirm and apply to all selected stores

## Bulk Action Buttons

When you select 2 or more stores, you'll see:
- **Edit Hours** - Update hours for selected stores
- **Edit Channels** - Toggle channels for selected stores
- **86 Items** - Snooze items for selected stores

## Best Practices

- **Start small** - Try with 2-3 stores first
- **Double-check selection** - Make sure you have the right stores
- **Review after** - Check the Activity Log to confirm changes
    `,
    tags: ['bulk', 'multiple stores', 'overview'],
    relatedArticles: ['bulk-hours', 'bulk-channels', 'bulk-items'],
  },
  {
    id: 'bulk-channels',
    slug: 'bulk-channels',
    title: 'Bulk Channel Updates',
    category: 'Bulk Operations',
    categorySlug: 'bulk-operations',
    summary: 'Turn channels on or off for multiple stores at once.',
    content: `
# Bulk Channel Updates

Need to pause delivery across your area during a storm? Bulk channel updates make it easy.

## Steps

1. Go to **Store Management** on desktop
2. Select multiple stores using checkboxes
3. Click **Edit Channels** in the action bar
4. Choose which channels to update:
   - Toggle Online Ordering on/off
   - Toggle specific delivery partners on/off
5. Add a reason (recommended)
6. Click **Apply**

## What Happens

- All selected stores receive the channel update
- Changes apply immediately
- Activity Log shows the bulk update

## Use Cases

- **Weather events** - Pause delivery during storms
- **System issues** - Turn off channels during POS outages
- **Promotions** - Enable a channel for a marketing push
- **End of day** - Ensure all channels are off at close

**Tip:** You can turn channels back on individually or in bulk when ready.
    `,
    tags: ['bulk', 'channels', 'delivery'],
    relatedArticles: ['bulk-overview', 'channels-toggle'],
  },
  {
    id: 'bulk-items',
    slug: 'bulk-items',
    title: 'Bulk Item Snooze',
    category: 'Bulk Operations',
    categorySlug: 'bulk-operations',
    summary: 'Snooze the same item across multiple stores.',
    content: `
# Bulk Item Snooze

Supplier didn't deliver an ingredient? Snooze affected items across all your stores at once.

## Steps

1. Go to **Store Management** on desktop
2. Select the affected stores using checkboxes
3. Click **86 Items** in the action bar
4. Search for and select the item(s)
5. Choose the snooze duration
6. Click **Apply**

## Duration Options

Same as individual snoozes: 1 H, 2 H, 4 H, 6 H, 12 H, End of Day (5 AM), 24 Hours, 1 Week,
1 Month, 2 Months, or a custom time up to 60 days out.

## Use Cases

- **Supply chain issues** - Ingredient unavailable across region
- **Product recalls** - Remove item from all locations
- **Seasonal items** - End a promotion simultaneously

## Auto-Restore

All snoozed items will auto-restore when their timers end, just like individual snoozes.

**Important:** Bulk snooze uses the same duration for all stores.
    `,
    tags: ['bulk', 'snooze', '86', 'items'],
    relatedArticles: ['bulk-overview', 'items-snooze'],
  },

  // Activity
  {
    id: 'activity-viewing',
    slug: 'viewing-activity',
    title: 'Viewing the Activity Log',
    category: 'Activity & History',
    categorySlug: 'activity',
    summary: 'Track all changes made in Store Control.',
    content: `
# Viewing the Activity Log

The Activity Log shows all changes made in Store Control. It's your audit trail for accountability and troubleshooting.

## Accessing the Activity Log

### Desktop
- Click **Activity Log** in the left sidebar

### Mobile
1. Tap **More** in the bottom navigation
2. Tap **Activity Log**

## What's in the Log

Each entry shows:
- **What changed** - Hours, channels, items
- **Who made the change** - User name
- **When** - Date and time
- **Details** - Specific values changed

## Filtering the Log

Filter by:
- **Store** - See activity for specific locations
- **Action type** - Hours, channels, items only
- **User** - See what a specific person changed
- **Date range** - Last 24 hours, week, month, etc.

## My Activity

To see only your own changes:
- Desktop: Click your name > **My Activity**
- Mobile: More > **My Activity**
    `,
    tags: ['activity', 'log', 'audit', 'history'],
    relatedArticles: ['activity-filtering', 'activity-audit'],
  },
  {
    id: 'activity-filtering',
    slug: 'filtering-activity',
    title: 'Filtering Activity',
    category: 'Activity & History',
    categorySlug: 'activity',
    summary: 'Find specific changes in your activity log.',
    content: `
# Filtering Activity

The Activity Log can have many entries. Filters help you find what you need.

## Available Filters

| Filter | Options |
|--------|---------|
| **Store** | Any store you have access to |
| **Action** | Hours, Channels, Items, All |
| **User** | Team members with access |
| **Date** | Today, Last 7 days, Last 30 days, Custom |

## Combining Filters

Filters stack. For example:
- Store: Downtown + Action: Channels + Date: Last 7 days
- Shows: All channel changes at Downtown in the past week

## Clearing Filters

- Click **Clear Filters** to reset all
- Or click the X on individual filter chips

## Exporting (Coming Soon)

Future versions will let you export filtered activity to CSV for reporting.
    `,
    tags: ['activity', 'filter', 'search'],
    relatedArticles: ['activity-viewing'],
  },
  {
    id: 'activity-audit',
    slug: 'audit-trail',
    title: 'Understanding the Audit Trail',
    category: 'Activity & History',
    categorySlug: 'activity',
    summary: 'How Store Control tracks changes for accountability.',
    content: `
# Understanding the Audit Trail

Every change in Store Control is logged automatically. This creates accountability and helps resolve questions about what happened and when.

## What Gets Logged

- **All hours changes** - Regular and special
- **Channel toggles** - On/off with reasons
- **Item snoozes** - Start, extend, restore
- **Bulk operations** - Logged as bulk with affected stores

## Who Can See What

- **RGMs** - See activity for their stores
- **ARLs** - See activity for all stores in their area
- **Operations** - See activity based on their access level

## Log Retention

Activity logs are kept for 90 days. For longer retention, export data periodically (feature coming soon).

## Using the Audit Trail

- **Troubleshooting** - "Why was my store closed yesterday?"
- **Training** - Review what new team members are doing
- **Accountability** - Verify who made specific changes
- **Patterns** - Identify recurring issues
    `,
    tags: ['audit', 'accountability', 'tracking'],
    relatedArticles: ['activity-viewing', 'activity-filtering'],
  },

  // Troubleshooting
  {
    id: 'trouble-common',
    slug: 'common-issues',
    title: 'Common Issues',
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    summary: 'Solutions to frequently encountered problems.',
    content: `
# Common Issues

Here are solutions to the most common problems users encounter.

## "My changes aren't showing up"

**Wait a few minutes.** Changes take 2-5 minutes to propagate to customer-facing apps and delivery partners.

If it's been more than 10 minutes:
1. Refresh the page
2. Check the Activity Log to confirm the change saved
3. If not in the log, try making the change again

## "I can't find my store"

- Make sure you're logged in with the correct account
- Your access is based on your role - contact your ARL if you should have access
- Try searching by store number instead of name

## "The app is loading slowly"

1. Check your internet connection
2. Try refreshing the page
3. Clear your browser cache
4. Try a different browser

## "I turned off a channel but orders keep coming"

- Orders already in the queue will complete
- New orders should stop within 2-5 minutes
- If orders continue, contact support

## "My snooze didn't work"

1. Check the Activity Log - did it save?
2. Try snooping the item again
3. Make sure you selected the correct store
    `,
    tags: ['troubleshooting', 'issues', 'problems', 'help'],
    relatedArticles: ['trouble-errors', 'trouble-contact'],
  },
  {
    id: 'trouble-errors',
    slug: 'error-messages',
    title: 'Error Messages Explained',
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    summary: 'What common error messages mean and how to fix them.',
    content: `
# Error Messages Explained

Here's what different error messages mean and how to resolve them.

## "Session expired"

Your login has timed out for security.

**Fix:** Refresh the page. You'll be redirected to log in again.

## "Failed to save changes"

The server couldn't save your update.

**Fix:**
1. Check your internet connection
2. Wait a moment and try again
3. Refresh and retry if it persists

## "Store not found"

The store you're looking for doesn't exist or you don't have access.

**Fix:**
1. Verify the store number
2. Check that you have access to this store
3. Contact your ARL if you should have access

## "Unable to connect"

Can't reach the server.

**Fix:**
1. Check your internet connection
2. Wait and refresh
3. Try again in a few minutes

## "Permission denied"

You don't have permission for this action.

**Fix:** Contact your ARL or administrator to request access.
    `,
    tags: ['errors', 'troubleshooting', 'messages'],
    relatedArticles: ['trouble-common', 'trouble-contact'],
  },
  {
    id: 'trouble-contact',
    slug: 'contact-support',
    title: 'Contact Support',
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    summary: 'How to get help when you need it.',
    content: `
# Contact Support

Still stuck? Here's how to get help.

## Before Contacting Support

Try these steps first:
1. Check this Help Center for solutions
2. Refresh the page and try again
3. Clear your browser cache
4. Try a different browser or device

## What to Include

When contacting support, include:
- **Your store number(s)**
- **What you were trying to do**
- **What happened instead**
- **Screenshots** if possible
- **Time it occurred**

## Support Channels

### For Store-Level Issues
Contact your Area Restaurant Leader (ARL) first. They can help with:
- Access issues
- Training questions
- Operational guidance

### For Technical Issues
If your ARL can't resolve it, they'll escalate to the technical support team.

### Emergency Issues
For urgent issues affecting customer orders:
- Contact your ARL immediately
- Use backup procedures until resolved
    `,
    tags: ['support', 'help', 'contact'],
    relatedArticles: ['trouble-common', 'trouble-errors'],
  },
];

// Helper functions
export function getArticlesByCategory(categorySlug: string): HelpArticle[] {
  return helpArticles.filter((article) => article.categorySlug === categorySlug);
}

export function getArticleById(id: string): HelpArticle | undefined {
  return helpArticles.find((article) => article.id === id);
}

export function getArticleBySlug(categorySlug: string, articleSlug: string): HelpArticle | undefined {
  return helpArticles.find(
    (article) => article.categorySlug === categorySlug && article.slug === articleSlug
  );
}

export function getCategoryBySlug(slug: string): HelpCategory | undefined {
  return helpCategories.find((category) => category.slug === slug);
}

export function searchArticles(query: string): HelpArticle[] {
  const lowerQuery = query.toLowerCase();
  return helpArticles.filter(
    (article) =>
      article.title.toLowerCase().includes(lowerQuery) ||
      article.summary.toLowerCase().includes(lowerQuery) ||
      article.content.toLowerCase().includes(lowerQuery) ||
      article.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
}
